import React from 'react';
import BookPortfolio from '../components/BookPortfolio';
import ProjectSingleImage from '../components/ProjectSingleImage';
import ProjectGallery from '../components/ProjectGallery';

export default function Projects() {
  // Your existing project data objects
  const customizedBearingHousing = {
    name: "Customized Bearing Housing Design",
    time: "January 2025 – May 2025",
    description: "This was the first design I engineered during my first Co-op term.",
    images: [
      "/images/projects/bearing-housing/BearingHousing.png",
      "/images/projects/bearing-housing/PlateBase.png",
      "/images/projects/bearing-housing/Plate.png",
      "/images/projects/bearing-housing/BearingBolts.png"
    ],
    captions: [
      "Bearing Housing View",
      "Plate Base View",
      "Bearing Plate Base View",
      "Bearing Bolts View"
    ]
  };

  const removableHandrails = {
    name: "Removable Handrails",
    time: "January 2025 – May 2025",
    description: "With guidance from amazing colleagues, I designed safety removable handrails for a platform. I learned many vital mechanical concepts that allow something to be 'removable' or 'adjustable'. Update: If I recall correctly, we ended up splitting the longer handrail into two components to ensure further safety.",
    images: [
      "/images/projects/2138/handrails/plan-view.png",
      "/images/projects/2138/handrails/parts-list.png",
      "/images/projects/2138/handrails/detailed-views1.png",
      "/images/projects/2138/handrails/detailed-views2.png",
      "/images/projects/2138/handrails/detailed-views3.png",
      "/images/projects/2138/handrails/detailed-views4.png"
    ],
    captions: [
      "Plan View",
      "Detailed Parts List",
      "Detailed Parts View 1",
      "Detailed Parts View 2",
      "Detailed Parts View 3",
      "Detailed Parts View 4"
    ]
  };

  const conveyorDesign = {
    name: "Conveyor Design",
    time: "January 2025 – May 2025",
    description: "This was the first major contribution I made for a project. With guidance from prior sources, structural analysis calculations, and communication with suppliers and colleagues, I designed a conveyor system and coordinated the respective manufacturing process.",
    images: [
      "/images/projects/2142/plan-view.png",
      "/images/projects/2142/decline-view.png",
      "/images/projects/2142/incline-view.png",
      "/images/projects/2142/washer-cross-section.png",
      "/images/projects/2142/detailed1.png",
      "/images/projects/2142/detailed2.png",
      "/images/projects/2142/detailed3.png",
      "/images/projects/2142/detailed4.png"
    ],
    captions: [
      "Plan View",
      "Decline View",
      "Incline View",
      "Washer Cross Section",
      "Detailed View 1",
      "Detailed View 2",
      "Detailed View 3",
      "Detailed View 4"
    ]
  };

  const rectangleToCircle = {
    name: "Rectangle to Circle Layout",
    time: "January 2025 - May 2025",
    description: "There are various recurring three dimensional shaped duct parts in the construction industry. Through a small lecture from a colleague and by referencing past drawings and physical components in the shop, I learned to draw one of the more complex ducts: rectangle to circle. To do this, I first drew a square to circle duct. After attempting and completing this task, I also learned about the equations that can be used for drawing this layout. This part can be used elsewhere too and the project formally introduced me to the AutoCAD 3D feature I use later in my internship.",
    images: [
      "/images/projects/misc/3dview.png",
      "/images/projects/misc/2dplan.png",
      "/images/projects/misc/2delevation.png",
      "/images/projects/misc/layout.png"
    ],
    captions: [
      "3D View of Rectangle to Circle Duct",
      "2D Plan View Layout",
      "2D Elevation Drawing",
      "Flat Pattern Layout for Manufacturing"
    ]
  };

  const platformExtensions = {
    name: "36\" and 48\" Platform Extensions",
    time: "January 2025 - May 2025",
    description: "I designed two different platform extensions for an existing cooling tunnel, by using industrial panels. An example of a part list I created is also included below.",
    images: [
      "/images/projects/3100/36inch.png",
      "/images/projects/3100/48inch.png",
      "/images/projects/3100/detailed1.png",
      "/images/projects/3100/detailed2.png",
      "/images/projects/3100/detailed3.png"
    ],
    captions: [
      "36 Inch Platform Extension Design",
      "48 Inch Platform Extension Design",
      "Detailed Parts List",
      "Detailed Parts List Continued",
      "Detailed Parts List Continued 2"
    ]
  };

  const mixingTankProject = {
    name: "Mixing Tank Project Lead",
    time: "January 2025 - May 2025",
    description: "This was one of the largest tasks I was given, and I am forever grateful for the experience. Essentially I led a waste water treatment project by which I designed and engineered a chemical mixing tank system through provided information, specified requirements, cost analysis, guidance from coworkers, necessary hydraulic equations, and so much more. After designing and refining the drawings, the design was approved, by which I ordered parts according to the shop inventory. Ultimately, I guided the mechanics in the shop through constructing the final project to be shipped to the client. Overall, I led a project and learned vital lessons about engineering and project management.",
    images: [
      "/images/projects/3022/flowdiagram.png",
      "/images/projects/3022/generallayout.png",
      "/images/projects/3022/pumpframe.png"
    ],
    captions: [
      "Process Flow Diagram",
      "General System Layout",
      "Pump Frame Design"
    ]
  };

  const blowOffSystemFrame = {
    name: "Blow Off System Frame",
    time: "January 2025 – May 2025",
    description: "I designed and created a blow off system component that was added to the general layout for a paint finishing system. To do so, I used Solid Works to create a reference of a blow off system that I then designed in AutoCAD.",
    images: [
      "/images/projects/2143/leftend.png",
      "/images/projects/2143/elevation.png",
      "/images/projects/2143/rightend.png",
      "/images/projects/2143/plan.png",
      "/images/projects/2143/enclosure.png",
      "/images/projects/2143/detailed.png"
    ],
    captions: [
      "Left End View",
      "Elevation View",
      "Right End View",
      "Plan View",
      "Sound Enclosure",
      "Detailed View Parts List"
    ]
  };

  const beltConveyorSystems = {
    name: "Belt Conveyor Systems",
    time: "January 2025 – May 2025",
    description: "I designed 2 belt conveyor systems of different dimensions and functionalities through consolidating all my knowledge of how different mechanical parts operate through my internship, consulting with mechanics, seeing how each part operates within the shop, and through consulting previous drawings. After gathering all these information, I designed two belt conveyor systems according to the required specifications. Note that the 12 inches and 24 inches refers to the width of the chain.",
    images: [
      "/images/projects/3286/12inch.png",
      "/images/projects/3286/24inch.png"
    ],
    captions: [
      "12 Inch Belt Conveyor System",
      "24 Inch Belt Conveyor System"
    ]
  };

  // Map your existing components to project keys (in the exact order from your Projects.jsx)
  const projectComponents = {
    rotationTracker: (
      <ProjectSingleImage
        title="Rotation Tracker Sensor Circuit"
        time="February 2024 – May 2024"
        description="For my culminating computer engineering project, I created an elaborate rotation tracker circuit, that determined the direction of rotation. For the sensor circuit, I created PCB using Eagle CAD and have attached the design below. During the design, I optimized the resources used to result in cost efficiency."
        imageSrc="/images/projects/temp/rotation_tracker_circuit.png"
        imageCaption="PCB Design for Rotation Tracker Circuit"
      />
    ),
    
    residenceProposal: (
      <ProjectSingleImage
        title="Waterloo University Residence Proposal"
        time="September 2024 - December 2024"
        description="Our team designed and proposed a sustainable residence to align with building codes, zoning regulations, and green design principles. Land conditions were considered to produce a cost-effective community-oriented design that prioritized pedestrian flow, stormwater management, and energy efficiency. All designs were created using AutoCAD Civil 3D and Infraworks."
        imageSrc="/images/projects/MHR-Project.png"
        imageCaption="MHR Proposal Showcase Poster"
      />
    ),

    customizedBearingHousing: (
      <ProjectGallery project={customizedBearingHousing} />
    ),

    wasteWaterSchematic: (
      <ProjectSingleImage
        title="Waste Water Treatment Schematic Updates"
        time="January 2025 – May 2025"
        description="As my first contribution for a large project, I updated and refined the waste water treatment schematic for a paint finishing project."
        imageSrc="/images/projects/2139/WasteWaterSchematic.jpg"
        imageCaption="Waste Water Treatment Schematic"
      />
    ),

    removableHandrails: (
      <ProjectGallery project={removableHandrails} />
    ),

    conveyorDesign: (
      <ProjectGallery project={conveyorDesign} />
    ),
    
    rectangleToCircle: (
      <ProjectGallery project={rectangleToCircle} />
    ),

    platformExtensions: (
      <ProjectGallery project={platformExtensions} />
    ),

    mixingTankProject: (
      <ProjectGallery project={mixingTankProject} />
    ),

    riserDesign: (
      <ProjectSingleImage
        title="Riser Design Replacement"
        time="January 2025 - May 2025"
        description="In waste water treatment systems, paint finishing systems, and most manufacturing firms, washers are used. In these washers, risers are commonly used. Note that risers are similar to shower and sprinkler systems but for mechanical parts. Considerably, a set of risers were to be replaced, so I took the initiative to learn to design a riser system."
        imageSrc="/images/projects/2142/risers.png"
        imageCaption="Riser System Design"
      />
    ),

    combustionChamber: (
      <ProjectSingleImage
        title="3D Model Assistance View for Combustion Chamber"
        time="January 2025 – May 2025"
        description="I used the AutoCAD 3D model to CAD a visaul enhancement for how the combustion chamber looks for one of our projects to optimize manufacturing times. Below is one of the drawings I made to do so."
        imageSrc="/images/projects/2142/3dmodel.png"
        imageCaption="3D Model View"
      />
    ),

    blowOffSystemFrame: (
      <ProjectGallery project={blowOffSystemFrame} />
    ),

    beltConveyorSystems: (
      <ProjectGallery project={beltConveyorSystems} />
    ),

    fanHousing: (
      <ProjectSingleImage
        title="Fan Housing Design"
        time="January 2025 – May 2025"
        description="Unfortunately, my internship was coming towards an end. However, I made many meaningful and unforgettable memories, built crucial strong relationships, and learned valuable transferrable lessons. In my last 2 days of interning, my colleagues and I each designed fan housings for 1-2 fans, one of my most cherishable moments of my first internship. Below is one of the fanhousing designs I created through AutoCAD and from consulting the fan's datasheet for necessary values."
        imageSrc="/images/projects/2143/fanhousing.png"
        imageCaption="Fan Housing Design"
      />
    )
  };

  return <BookPortfolio projectComponents={projectComponents} />;
}